import { List } from "immutable";
import { AnyAction } from "redux";

import {
  LOCATIONS_UPDATE_PETS_ENDPOINT,
  LOCATIONS_ENDPOINT,
  LOCATIONS_SET_DEFAULT_ENDPOINT,
  UNCOVERED_LOCATIONS_ENDPOINT,
  B2B_LOCATIONS_ENDPOINT,
  ZIP_SUGGESTION_ENDPOINT,
  LOCATION_BY_UUID_ENDPOINT,
} from "@config/endpoints";

import {
  ThunkActionDispatch,
  ThunkActionOptions,
} from "@typings/reduxThunkActions";
import { SERVICE_TYPE } from "@typings/globals";
import { ZipSuggestion } from "@typings/locations";

import { buildUrl } from "@services/BuildUrl";
import { ASYNC_ACTION_TYPES } from "@services/FetchFacade";
import { ReduxActionNameCreator } from "@services/ReduxActionNameCreator";
import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";

import { IStore } from "@app/redux/reducers";
import {
  setLocalStorageHelper,
  setFromLocalStorageHelper,
  deleteLocalStorageHelper,
  abortControllerHelper,
} from "@app/redux/helpers";
import { asyncActionCreator } from "@app/services/AsyncActionCreator";
import { genericPendingAction } from "@app/services/GenericPendingAction";

import { IPetsUpdate, LOCAL_STORAGE_KEY } from "./locations.typings";

interface FetchZipCodesSuggestionsParams {
  query: string;
  serviceType: string;
  signal?: AbortSignal;
}

const action = new ReduxActionNameCreator("jobs");

const abortControllers = {
  fetchLocations: abortControllerHelper(),
};

// Actions
export const FETCH_LOCATIONS = action.make("fetch locations");
export const FETCH_B2B_LOCATIONS = action.make("fetch b2b locations");
export const UPDATE_PETS = action.make("update pets");
export const SET_DEFAULT_LOCATION = action.make("set default location");
export const SEND_UNCOVERED_LOCATION = action.make("send uncovered location");
export const SET_IN_LOCAL_STORAGE = action.make("set in local storage");
export const SET_FROM_LOCAL_STORAGE = action.make("set from local storage");
export const DELETE_LOCAL_STORAGE = action.make("delete local storage");
export const FETCH_ZIP_CODES_SUGGESTIONS = action.make(
  "fetch zip codes suggestions",
);
export const SET_ZIP_CODES_SUGGESTIONS = action.make(
  "set zip codes suggestions",
);
export const DELETE_LOCATION = action.make("delete location");

// Actions creators
export const fetchLocations =
  (opts?: ThunkActionOptions) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch({
      type: FETCH_LOCATIONS,
    });

    const action = await asyncActionCreator(
      {
        url: LOCATIONS_ENDPOINT,
        action: FETCH_LOCATIONS,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchLocations(),
        }),
      },
    );

    return dispatch(action);
  };

export const fetchB2BLocations =
  () =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch({
      type: FETCH_B2B_LOCATIONS,
    });

    const action = await asyncActionCreator({
      url: B2B_LOCATIONS_ENDPOINT,
      action: FETCH_B2B_LOCATIONS,
    });

    return dispatch(action);
  };

export const updatePets =
  (id: string, body: IPetsUpdate) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const url = LOCATIONS_UPDATE_PETS_ENDPOINT.replace("{uuid}", id);

    const action = await asyncActionCreator({
      url,
      body,
      params: {
        ...body,
        id,
      },
      action: UPDATE_PETS,
      method: ASYNC_ACTION_TYPES.POST,
    });

    return dispatch(action);
  };

export const setLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch, store: () => IStore): void => {
    setLocalStorageHelper(
      dispatch,
      SET_IN_LOCAL_STORAGE,
      store().locations,
      LOCAL_STORAGE_KEY,
    );
  };

export const setFromLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch): void => {
    setFromLocalStorageHelper(
      dispatch,
      SET_FROM_LOCAL_STORAGE,
      LOCAL_STORAGE_KEY,
    );
  };

export const deleteLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch): void => {
    deleteLocalStorageHelper(dispatch, DELETE_LOCAL_STORAGE, LOCAL_STORAGE_KEY);
  };

export const setLocationAsDefault =
  (locationUuid: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: LOCATIONS_SET_DEFAULT_ENDPOINT,
      action: SET_DEFAULT_LOCATION,
      method: ASYNC_ACTION_TYPES.PUT,
      body: { locationUuid },
    });

    return dispatch(action);
  };

export const sendUncoveredLocation =
  ({
    email,
    zipCode,
    serviceType,
  }: {
    email: string;
    zipCode: string;
    serviceType: SERVICE_TYPE | null;
  }) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: UNCOVERED_LOCATIONS_ENDPOINT,
      action: SEND_UNCOVERED_LOCATION,
      method: ASYNC_ACTION_TYPES.POST,
      body: {
        email,
        zipCode,
        serviceType,
      },
    });

    return dispatch(action);
  };

export const fetchZipCodesSuggestions =
  ({ query, serviceType, signal }: FetchZipCodesSuggestionsParams) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator(
      {
        url: buildUrl(ZIP_SUGGESTION_ENDPOINT)
          .setQuery("q", query.trim())
          .setQuery("type", serviceType)
          .done(),
        action: FETCH_ZIP_CODES_SUGGESTIONS,
        method: ASYNC_ACTION_TYPES.GET,
      },
      {
        signal,
      },
    );

    return dispatch(action);
  };

export const setZipCodesSuggestions = (
  payload: List<ZipSuggestion>,
): AnyAction => ({
  type: SET_ZIP_CODES_SUGGESTIONS,
  payload,
});

export const deleteLocation =
  (locationUuid: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(DELETE_LOCATION, { locationUuid }));

    const url = LOCATION_BY_UUID_ENDPOINT.replace("{uuid}", locationUuid);
    const action = await asyncActionCreator({
      url,
      params: { locationUuid },
      action: DELETE_LOCATION,
      method: ASYNC_ACTION_TYPES.DELETE,
    });

    return dispatch(action);
  };
